import { importTranscriptInputSchema } from '@sl/shared';

/**
 * Parsers for the "paste or upload an existing transcript" flow (scope §7.1's
 * alternative capture path). All three formats collapse to the same shape the
 * import API expects, so the Upload page can build one request body no matter
 * which format the reviewer pasted.
 */

export interface ParsedTranscriptSegment {
  speaker: string;
  startSec: number;
  endSec: number;
  text: string;
}

export interface ParsedTranscriptSpeaker {
  label: string;
  name: string;
}

export interface TranscriptImportResult {
  provider?: string;
  model?: string;
  language?: 'ar' | 'en' | 'mixed';
  speakers?: ParsedTranscriptSpeaker[];
  segments: ParsedTranscriptSegment[];
  /** Cues or lines that did not match the expected shape and were dropped. */
  skipped: number;
}

export class TranscriptImportError extends Error {}

/** A cue or line with no recognizable speaker prefix falls back to this. */
const DEFAULT_SPEAKER = 'Speaker 1';

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// --------------------------------------------------------------- WebVTT ----

/** "HH:MM:SS.mmm" or "MM:SS.mmm" -> seconds; null when it does not parse. */
function parseVttTimestamp(raw: string): number | null {
  const m = raw.trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{1,3})$/);
  if (!m) return null;
  const hours = m[1] ? Number(m[1]) : 0;
  const minutes = Number(m[2]);
  const seconds = Number(m[3]);
  const millis = Number(m[4]!.padEnd(3, '0'));
  if ([hours, minutes, seconds, millis].some((v) => Number.isNaN(v))) return null;
  return round(hours * 3600 + minutes * 60 + seconds + millis / 1000);
}

// Zoom's cue text is "Speaker Name: the words"; the colon must be followed by
// whitespace so a mid-sentence colon in plain prose does not look like one.
const SPEAKER_PREFIX = /^([^:\n]{1,80}):[ \t](.*)$/;

function splitSpeaker(text: string): { speaker: string; text: string } {
  const m = text.match(SPEAKER_PREFIX);
  if (!m) return { speaker: DEFAULT_SPEAKER, text: text.trim() };
  const speaker = m[1]!.trim();
  const rest = m[2]!.trim();
  if (!speaker || !rest) return { speaker: DEFAULT_SPEAKER, text: text.trim() };
  return { speaker, text: rest };
}

export function parseVtt(input: string): TranscriptImportResult {
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  const segments: ParsedTranscriptSegment[] = [];
  let skipped = 0;
  let i = 0;

  // The WEBVTT header (plus any NOTE/metadata block) runs up to the first
  // blank line; skip it rather than trying to parse it as a cue.
  if (lines[0]?.trim().toUpperCase().startsWith('WEBVTT')) {
    while (i < lines.length && lines[i]!.trim() !== '') i += 1;
  }

  while (i < lines.length) {
    while (i < lines.length && lines[i]!.trim() === '') i += 1;
    if (i >= lines.length) break;

    // Optional numeric/identifier line before the timing line.
    if (!lines[i]!.includes('-->') && i + 1 < lines.length && lines[i + 1]!.includes('-->')) {
      i += 1;
    }

    const timingLine = lines[i] ?? '';
    i += 1;
    const timingMatch = /([\d:.,]+)\s*-->\s*([\d:.,]+)/.exec(timingLine);
    if (!timingMatch) {
      skipped += 1;
      // Consume the rest of this malformed block up to the next blank line.
      while (i < lines.length && lines[i]!.trim() !== '') i += 1;
      continue;
    }

    const textLines: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== '') {
      textLines.push(lines[i]!);
      i += 1;
    }

    const startSec = parseVttTimestamp(timingMatch[1]!);
    const endSec = parseVttTimestamp(timingMatch[2]!);
    const rawText = textLines.join(' ').trim();
    if (startSec === null || endSec === null || endSec < startSec || !rawText) {
      skipped += 1;
      continue;
    }

    const { speaker, text } = splitSpeaker(rawText);
    segments.push({ speaker, startSec, endSec, text });
  }

  return { segments, skipped };
}

// ------------------------------------------------------------ plain text ---

// "[hh:mm:ss] Speaker: text" or "[mm:ss] Speaker: text".
const PLAIN_LINE = /^\[(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\]\s*([^:\n]{1,80}):\s*(.+)$/;

export function parsePlainText(input: string): TranscriptImportResult {
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  const parsed: { startSec: number; speaker: string; text: string }[] = [];
  let skipped = 0;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = PLAIN_LINE.exec(line);
    if (!m) {
      skipped += 1;
      continue;
    }
    const hours = m[1] ? Number(m[1]) : 0;
    const startSec = round(hours * 3600 + Number(m[2]) * 60 + Number(m[3]));
    const speaker = m[4]!.trim();
    const text = m[5]!.trim();
    if (!speaker || !text) {
      skipped += 1;
      continue;
    }
    parsed.push({ startSec, speaker, text });
  }

  const segments: ParsedTranscriptSegment[] = parsed.map((p, i) => {
    const next = parsed[i + 1];
    const endSec = next ? next.startSec : round(p.startSec + 3);
    return { speaker: p.speaker, startSec: p.startSec, endSec, text: p.text };
  });

  return { segments, skipped };
}

// ------------------------------------------------------------------ JSON ---

export function parseJsonTranscript(input: string): TranscriptImportResult {
  let data: unknown;
  try {
    data = JSON.parse(input);
  } catch {
    throw new TranscriptImportError('That is not valid JSON.');
  }
  const parsed = importTranscriptInputSchema.safeParse(data);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const where = first?.path.length ? ` (${first.path.join('.')})` : '';
    throw new TranscriptImportError(`Invalid transcript JSON${where}: ${first?.message ?? 'unknown error'}`);
  }
  const { provider, model, language, speakers, segments } = parsed.data;
  return {
    provider,
    model,
    language,
    speakers,
    segments: segments.map((s) => ({
      speaker: s.speaker,
      startSec: s.startSec,
      endSec: s.endSec,
      text: s.text,
    })),
    skipped: 0,
  };
}

// -------------------------------------------------------------- dispatch ---

/** Sniffs the format from the content itself, so the page needs no format picker. */
export function parseTranscriptImport(raw: string): TranscriptImportResult {
  const trimmed = raw.trim();
  if (!trimmed) throw new TranscriptImportError('Paste or choose a transcript file first.');
  if (trimmed.toUpperCase().startsWith('WEBVTT')) return parseVtt(raw);
  if (trimmed.startsWith('{')) return parseJsonTranscript(raw);
  if (trimmed.startsWith('[')) {
    // A plain-text line also starts with "[" (its timestamp), so only treat
    // this as JSON when it actually parses as JSON syntax.
    try {
      JSON.parse(trimmed);
    } catch {
      return parsePlainText(raw);
    }
    return parseJsonTranscript(raw);
  }
  return parsePlainText(raw);
}
